import React, { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, Building2, UserCheck, Target, Clock, BarChart3, Briefcase, Save, X, RefreshCw, CheckCircle, XCircle, AlertCircle, Check, ChevronsUpDown, Crown, MapPin, Dumbbell, Settings, ChevronRight, ChevronDown } from 'lucide-react';
import { LocationDialog } from '@/components/location/LocationDialog';
import { GPSBadge } from '@/components/location/GPSBadge';
import { MapPreview } from '@/components/location/MapPreview';
import { extractCoordinates, validateCoordinates, formatCoordinates, isShortUrl } from '@/utils/googleMapsParser';
import { useWtfSyncStatus } from '@/hooks/useWtfLiveData';
import LeadSourceMappingTab from '@/components/config/LeadSourceMappingTab';

export default function MasterConfig() {
  const [managers, setManagers] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [fitnessManagers, setFitnessManagers] = useState([]);
  const [gyms, setGyms] = useState([]);
  const [sources, setSources] = useState([]);
  const [pulses, setPulses] = useState([]);
  const [benchmarks, setBenchmarks] = useState([]);
  const [targets, setTargets] = useState([]);
  const [frontDesk, setFrontDesk] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hierarchyData, setHierarchyData] = useState(null);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [mgrs, tls, fms, gymsData, srcs, pls, bms, tgts, fds] = await Promise.all([
        axiosInstance.get('/managers'),
        axiosInstance.get('/team-leaders'),
        axiosInstance.get('/fitness-managers'),
        axiosInstance.get('/gyms'),
        axiosInstance.get('/sources'),
        axiosInstance.get('/pulses'),
        axiosInstance.get('/benchmarks'),
        axiosInstance.get('/targets'),
        axiosInstance.get('/front-desk'),
      ]);
      setManagers(mgrs.data);
      setTeamLeaders(tls.data);
      setFitnessManagers(fms.data);
      setGyms(gymsData.data);
      setSources(srcs.data);
      setPulses(pls.data);
      setBenchmarks(bms.data);
      setTargets(tgts.data);
      setFrontDesk(fds.data);

      // Fetch users separately - admin only
      try {
        const usersData = await axiosInstance.get('/users');
        setUsers(usersData.data);
      } catch (e) {
        // Non-admin users can't fetch users list - silently ignore
        setUsers([]);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // ============= MANAGERS TAB =============
  const ManagersTab = () => {
    const [open, setOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [formData, setFormData] = useState({ manager_id: '', name: '', email: '', phone: '', status: 'Active' });

    const resetForm = () => setFormData({ manager_id: '', name: '', email: '', phone: '', status: 'Active' });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.post('/managers', { ...formData, status: formData.status || 'Active' });
        toast.success('Manager added successfully');
        setOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to add manager');
      }
    };

    const handleEdit = (item) => {
      setEditItem(item);
      setFormData({ ...item });
      setEditOpen(true);
    };

    const handleUpdate = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.put(`/managers/${editItem.manager_id}`, formData);
        toast.success('Manager updated successfully');
        setEditOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to update manager');
      }
    };

    const handleDelete = async (id) => {
      if (window.confirm('Are you sure you want to delete this manager?')) {
        try {
          await axiosInstance.delete(`/managers/${id}`);
          toast.success('Manager deleted');
          fetchAllData();
        } catch (error) {
          toast.error('Failed to delete manager');
        }
      }
    };

    const getTLsUnder = (managerId) => teamLeaders.filter(tl => tl.manager_id === managerId).length;
    const getGymsUnder = (managerId) => gyms.filter(g => g.manager_id === managerId).length;

    const FormFields = ({ isEdit = false }) => (
      <>
        <div className="grid grid-cols-2 gap-4">
          {isEdit ? (
            <div className="space-y-2">
              <Label>Manager ID</Label>
              <Input value={formData.manager_id} disabled className="bg-gray-100" />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Manager ID</Label>
              <Input value="(Auto-generated)" disabled className="bg-gray-100 text-gray-500 italic" />
            </div>
          )}
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Email *</Label>
            <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label>Phone *</Label>
            <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
          </div>
        </div>
        {isEdit && (
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </>
    );

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Users size={20} /> Managers ({managers.length})
          </h3>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-manager-btn"><Plus size={16} className="mr-2" /> Add Manager</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Manager</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <FormFields />
                <Button type="submit" className="w-full"><Plus size={16} className="mr-2" /> Add Manager</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) resetForm(); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Manager</DialogTitle></DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <FormFields isEdit />
              <Button type="submit" className="w-full"><Save size={16} className="mr-2" /> Save Changes</Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="data-table">
          <table>
            <thead>
              <tr><th>Manager ID</th><th>Name</th><th>Email</th><th>Phone</th><th>Status</th><th>TLs Under</th><th>Gyms Under</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {managers.map((mgr, idx) => (
                <tr key={idx} data-testid={`manager-row-${idx}`}>
                  <td>{mgr.manager_id}</td>
                  <td className="font-semibold">{mgr.name}</td>
                  <td>{mgr.email}</td>
                  <td>{mgr.phone}</td>
                  <td><span className={`status-badge ${mgr.status === 'Active' ? 'success' : 'error'}`}>{mgr.status}</span></td>
                  <td className="text-center font-semibold">{getTLsUnder(mgr.manager_id)}</td>
                  <td className="text-center font-semibold">{getGymsUnder(mgr.manager_id)}</td>
                  <td>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(mgr)} title="Edit"><Edit size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(mgr.manager_id)} title="Delete" className="text-red-600 hover:text-red-700"><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============= TEAM LEADERS TAB =============
  const TeamLeadersTab = () => {
    const [open, setOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [formData, setFormData] = useState({ tl_id: '', name: '', manager_id: '', manager_name: '', email: '', phone: '', status: 'Active' });

    const resetForm = () => setFormData({ tl_id: '', name: '', manager_id: '', manager_name: '', email: '', phone: '', status: 'Active' });

    const handleManagerChange = (managerId) => {
      const mgr = managers.find(m => m.manager_id === managerId);
      setFormData({ ...formData, manager_id: managerId, manager_name: mgr?.name || '' });
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.post('/team-leaders', { ...formData, status: formData.status || 'Active' });
        toast.success('Team Leader added successfully');
        setOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to add team leader');
      }
    };

    const handleEdit = (item) => {
      setEditItem(item);
      setFormData({ ...item });
      setEditOpen(true);
    };

    const handleUpdate = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.put(`/team-leaders/${editItem.tl_id}`, formData);
        toast.success('Team Leader updated successfully');
        setEditOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to update team leader');
      }
    };

    const handleDelete = async (id) => {
      if (window.confirm('Are you sure you want to delete this team leader?')) {
        try {
          await axiosInstance.delete(`/team-leaders/${id}`);
          toast.success('Team Leader deleted');
          fetchAllData();
        } catch (error) {
          toast.error('Failed to delete team leader');
        }
      }
    };

    const FormFields = ({ isEdit = false }) => (
      <>
        <div className="grid grid-cols-2 gap-4">
          {isEdit ? (
            <div className="space-y-2">
              <Label>TL ID</Label>
              <Input value={formData.tl_id} disabled className="bg-gray-100" />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>TL ID</Label>
              <Input value="(Auto-generated)" disabled className="bg-gray-100 text-gray-500 italic" />
            </div>
          )}
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Reporting Manager *</Label>
          <Select value={formData.manager_id} onValueChange={handleManagerChange}>
            <SelectTrigger><SelectValue placeholder="Select Manager" /></SelectTrigger>
            <SelectContent>
              {managers.map(mgr => (
                <SelectItem key={mgr.manager_id} value={mgr.manager_id}>{mgr.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Email *</Label>
            <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label>Phone *</Label>
            <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
          </div>
        </div>
        {isEdit && (
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </>
    );

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <UserCheck size={20} /> Team Leaders ({teamLeaders.length})
          </h3>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-tl-btn"><Plus size={16} className="mr-2" /> Add Team Leader</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Team Leader</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <FormFields />
                <Button type="submit" className="w-full"><Plus size={16} className="mr-2" /> Add Team Leader</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) resetForm(); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Team Leader</DialogTitle></DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <FormFields isEdit />
              <Button type="submit" className="w-full"><Save size={16} className="mr-2" /> Save Changes</Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="data-table">
          <table>
            <thead>
              <tr><th>TL ID</th><th>Name</th><th>Manager</th><th>Email</th><th>Phone</th><th>Status</th><th>Gyms</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {teamLeaders.map((tl, idx) => {
                const gymsCount = gyms.filter(g => g.tl_id === tl.tl_id).length;
                return (
                  <tr key={idx} data-testid={`tl-row-${idx}`}>
                    <td>{tl.tl_id}</td>
                    <td className="font-semibold">{tl.name}</td>
                    <td>{tl.manager_name}</td>
                    <td>{tl.email}</td>
                    <td>{tl.phone}</td>
                    <td><span className={`status-badge ${tl.status === 'Active' ? 'success' : 'error'}`}>{tl.status}</span></td>
                    <td className="text-center font-semibold">{gymsCount}</td>
                    <td>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(tl)} title="Edit"><Edit size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(tl.tl_id)} title="Delete" className="text-red-600 hover:text-red-700"><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============= FITNESS MANAGERS TAB =============
  const FitnessManagersTab = () => {
    const [open, setOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [formData, setFormData] = useState({ fm_id: '', name: '', manager_id: '', manager_name: '', email: '', phone: '', status: 'Active' });

    const resetForm = () => setFormData({ fm_id: '', name: '', manager_id: '', manager_name: '', email: '', phone: '', status: 'Active' });

    const handleManagerChange = (managerId) => {
      const mgr = managers.find(m => m.manager_id === managerId);
      setFormData({ ...formData, manager_id: managerId, manager_name: mgr?.name || '' });
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.post('/fitness-managers', { ...formData, status: formData.status || 'Active' });
        toast.success('Fitness Manager added successfully');
        setOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to add fitness manager');
      }
    };

    const handleEdit = (item) => {
      setEditItem(item);
      setFormData({ ...item });
      setEditOpen(true);
    };

    const handleUpdate = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.put(`/fitness-managers/${editItem.fm_id}`, formData);
        toast.success('Fitness Manager updated successfully');
        setEditOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to update fitness manager');
      }
    };

    const handleDelete = async (id) => {
      if (window.confirm('Are you sure you want to delete this fitness manager?')) {
        try {
          await axiosInstance.delete(`/fitness-managers/${id}`);
          toast.success('Fitness Manager deleted');
          fetchAllData();
        } catch (error) {
          toast.error('Failed to delete fitness manager');
        }
      }
    };

    const FormFields = ({ isEdit = false }) => (
      <>
        <div className="grid grid-cols-2 gap-4">
          {isEdit ? (
            <div className="space-y-2">
              <Label>FM ID</Label>
              <Input value={formData.fm_id} disabled className="bg-gray-100" />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>FM ID</Label>
              <Input value="(Auto-generated)" disabled className="bg-gray-100 text-gray-500 italic" />
            </div>
          )}
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Reporting Manager *</Label>
          <Select value={formData.manager_id} onValueChange={handleManagerChange}>
            <SelectTrigger><SelectValue placeholder="Select Manager" /></SelectTrigger>
            <SelectContent>
              {managers.map(mgr => (
                <SelectItem key={mgr.manager_id} value={mgr.manager_id}>{mgr.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Email *</Label>
            <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label>Phone *</Label>
            <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
          </div>
        </div>
        {isEdit && (
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </>
    );

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Dumbbell size={20} /> Fitness Managers ({fitnessManagers.length})
          </h3>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-fm-btn"><Plus size={16} className="mr-2" /> Add Fitness Manager</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Fitness Manager</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <FormFields />
                <Button type="submit" className="w-full"><Plus size={16} className="mr-2" /> Add Fitness Manager</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) resetForm(); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Fitness Manager</DialogTitle></DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <FormFields isEdit />
              <Button type="submit" className="w-full"><Save size={16} className="mr-2" /> Save Changes</Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="data-table">
          <table>
            <thead>
              <tr><th>FM ID</th><th>Name</th><th>Manager</th><th>Email</th><th>Phone</th><th>Status</th><th>Gyms</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {fitnessManagers.map((fm, idx) => {
                const gymsCount = gyms.filter(g => g.fm_id === fm.fm_id).length;
                return (
                  <tr key={idx} data-testid={`fm-row-${idx}`}>
                    <td>{fm.fm_id}</td>
                    <td className="font-semibold">{fm.name}</td>
                    <td>{fm.manager_name}</td>
                    <td>{fm.email}</td>
                    <td>{fm.phone}</td>
                    <td><span className={`status-badge ${fm.status === 'Active' ? 'success' : 'error'}`}>{fm.status}</span></td>
                    <td className="text-center font-semibold">{gymsCount}</td>
                    <td>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(fm)} title="Edit"><Edit size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(fm.fm_id)} title="Delete" className="text-red-600 hover:text-red-700"><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============= GYMS TAB =============
  const GymsTab = () => {
    const [assignOpen, setAssignOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [showUnmappedOnly, setShowUnmappedOnly] = useState(false);
    const [gymSearchText, setGymSearchText] = useState('');
    const [assignmentMode, setAssignmentMode] = useState('tl_manager_fm');
    const [gymSearchOpen, setGymSearchOpen] = useState(false);
    const [locationDialogOpen, setLocationDialogOpen] = useState(false);
    const [locationGym, setLocationGym] = useState(null);
    const [formData, setFormData] = useState({
      gym_id: '', tl_id: '', tl_name: '', manager_id: '', manager_name: '',
      fm_id: '', fm_name: '', admin_id: '', admin_name: '', assignment_mode: 'unassigned'
    });

    const resetForm = () => setFormData({
      gym_id: '', tl_id: '', tl_name: '', manager_id: '', manager_name: '',
      fm_id: '', fm_name: '', admin_id: '', admin_name: '', assignment_mode: 'unassigned'
    });

    const unmappedGyms = gyms.filter(g => !g.assignment_mode || g.assignment_mode === 'unassigned');
    const filteredByMapping = showUnmappedOnly ? unmappedGyms : gyms;
    const displayedGyms = gymSearchText.trim()
      ? filteredByMapping.filter(g => (g.name || '').toLowerCase().includes(gymSearchText.trim().toLowerCase()) || (g.gym_id || '').toLowerCase().includes(gymSearchText.trim().toLowerCase()))
      : filteredByMapping;

    const handleAssignmentModeChange = (mode) => {
      setAssignmentMode(mode);
      // Preserve existing assignments that are still relevant to the new mode
      const hasTL = mode === 'tl_manager' || mode === 'tl_manager_fm';
      const hasManager = mode === 'tl_manager' || mode === 'tl_manager_fm' || mode === 'manager_only';
      const hasFM = mode === 'tl_manager_fm' || mode === 'fm_only';
      const hasAdmin = mode === 'admin_only';
      setFormData({
        ...formData,
        assignment_mode: mode,
        tl_id: hasTL ? formData.tl_id : '', tl_name: hasTL ? formData.tl_name : '',
        manager_id: hasManager ? formData.manager_id : '', manager_name: hasManager ? formData.manager_name : '',
        fm_id: hasFM ? formData.fm_id : '', fm_name: hasFM ? formData.fm_name : '',
        admin_id: hasAdmin ? formData.admin_id : '', admin_name: hasAdmin ? formData.admin_name : '',
      });
    };

    const handleTLChange = (tlId) => {
      const tl = teamLeaders.find(t => t.tl_id === tlId);
      setFormData({ ...formData, tl_id: tlId, tl_name: tl?.name || '', manager_id: tl?.manager_id || '', manager_name: tl?.manager_name || '' });
    };

    const handleManagerChange = (managerId) => {
      const manager = managers.find(m => m.manager_id === managerId);
      setFormData({ ...formData, manager_id: managerId, manager_name: manager?.name || '' });
    };

    const handleFMChange = (fmId) => {
      const fm = fitnessManagers.find(f => f.fm_id === fmId);
      setFormData({ ...formData, fm_id: fmId, fm_name: fm?.name || '' });
    };

    const handleAdminChange = (adminId) => {
      const admin = users.find(u => u.user_id === adminId && u.role === 'admin');
      setFormData({ ...formData, admin_id: adminId, admin_name: admin?.name || '' });
    };

    const handleAssignSubmit = async (e) => {
      e.preventDefault();
      if (!formData.gym_id) return toast.error('Please select a gym');

      if (assignmentMode === 'tl_manager_fm' && (!formData.tl_id || !formData.manager_id || !formData.fm_id)) {
        return toast.error('Please select Team Leader, Manager, and Fitness Manager');
      } else if (assignmentMode === 'tl_manager' && (!formData.tl_id || !formData.manager_id)) {
        return toast.error('Please select both Team Leader and Manager');
      } else if (assignmentMode === 'manager_only' && !formData.manager_id) {
        return toast.error('Please select a Manager');
      } else if (assignmentMode === 'fm_only' && !formData.fm_id) {
        return toast.error('Please select a Fitness Manager');
      } else if (assignmentMode === 'admin_only' && !formData.admin_id) {
        return toast.error('Please select an Admin');
      }

      try {
        // Get full gym data and merge with assignment fields
        const selectedGym = gyms.find(g => g.gym_id === formData.gym_id);
        if (!selectedGym) return toast.error('Gym not found');

        const updateData = {
          ...selectedGym,  // Include all existing gym fields (name, location, etc.)
          ...formData,     // Override with assignment fields
          assignment_mode: assignmentMode
        };

        await axiosInstance.put(`/gyms/${formData.gym_id}`, updateData);
        toast.success('Gym assignment successful');
        setAssignOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to assign gym');
      }
    };

    const handleEdit = (item) => {
      setEditItem(item);
      setFormData({ ...item });
      setAssignmentMode(item.assignment_mode || 'tl_manager');
      setEditOpen(true);
    };

    const handleUpdate = async (e) => {
      e.preventDefault();
      try {
        // Merge editItem with formData to ensure all fields are included
        const updateData = {
          ...editItem,  // Include all existing gym fields
          ...formData   // Override with edited fields
        };
        await axiosInstance.put(`/gyms/${editItem.gym_id}`, updateData);
        toast.success('Gym updated successfully');
        setEditOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to update gym');
      }
    };

    const handleDelete = async (id) => {
      if (window.confirm('Are you sure you want to delete this gym?')) {
        try {
          await axiosInstance.delete(`/gyms/${id}`);
          toast.success('Gym deleted');
          fetchAllData();
        } catch (error) {
          toast.error('Failed to delete gym');
        }
      }
    };

    const handleSetLocation = (gym) => {
      setLocationGym(gym);
      setLocationDialogOpen(true);
    };

    const handleLocationSaved = (updatedGym) => {
      // Refresh gyms list to show updated coordinates
      fetchAllData();
    };

    const AssignmentFields = () => {
      const selectedGym = unmappedGyms.find(g => g.gym_id === formData.gym_id);

      return (
        <>
          <div className="space-y-2">
            <Label>Select Gym *</Label>
            <Popover open={gymSearchOpen} onOpenChange={setGymSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={gymSearchOpen}
                  className="w-full justify-between"
                >
                  {selectedGym ? `${selectedGym.gym_id} - ${selectedGym.name}` : "Search gym..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search gym by name, ID, or location..." />
                  <CommandList>
                    <CommandEmpty>No gym found.</CommandEmpty>
                    <CommandGroup>
                      {unmappedGyms.map((gym) => (
                        <CommandItem
                          key={gym.gym_id}
                          value={`${gym.gym_id} ${gym.name} ${gym.location}`}
                          onSelect={() => {
                            setFormData({ ...formData, gym_id: gym.gym_id });
                            setGymSearchOpen(false);
                          }}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${formData.gym_id === gym.gym_id ? "opacity-100" : "opacity-0"}`}
                          />
                          <div className="flex flex-col">
                            <span className="font-medium">{gym.gym_id} - {gym.name}</span>
                            <span className="text-xs text-muted-foreground">{gym.location}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

        <div className="space-y-2">
          <Label>Assignment Mode *</Label>
          <Select value={assignmentMode} onValueChange={handleAssignmentModeChange}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tl_manager_fm">TL + Manager + FM (Preferred)</SelectItem>
              <SelectItem value="tl_manager">TL + Manager Only</SelectItem>
              <SelectItem value="manager_only">Manager Only</SelectItem>
              <SelectItem value="fm_only">Fitness Manager Only</SelectItem>
              <SelectItem value="admin_only">Admin Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {assignmentMode === 'tl_manager_fm' && (
          <>
            <div className="space-y-2">
              <Label>Team Leader *</Label>
              <Select value={formData.tl_id} onValueChange={handleTLChange}>
                <SelectTrigger><SelectValue placeholder="Select Team Leader" /></SelectTrigger>
                <SelectContent>
                  {teamLeaders.map(tl => (
                    <SelectItem key={tl.tl_id} value={tl.tl_id}>{tl.name} ({tl.manager_name})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.manager_name && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <strong>Manager:</strong> {formData.manager_name} (auto-assigned from TL)
              </div>
            )}
            <div className="space-y-2">
              <Label>Fitness Manager *</Label>
              <Select value={formData.fm_id} onValueChange={handleFMChange}>
                <SelectTrigger><SelectValue placeholder="Select Fitness Manager" /></SelectTrigger>
                <SelectContent>
                  {fitnessManagers.map(fm => (
                    <SelectItem key={fm.fm_id} value={fm.fm_id}>{fm.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {assignmentMode === 'tl_manager' && (
          <>
            <div className="space-y-2">
              <Label>Team Leader *</Label>
              <Select value={formData.tl_id} onValueChange={handleTLChange}>
                <SelectTrigger><SelectValue placeholder="Select Team Leader" /></SelectTrigger>
                <SelectContent>
                  {teamLeaders.map(tl => (
                    <SelectItem key={tl.tl_id} value={tl.tl_id}>{tl.name} ({tl.manager_name})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.manager_name && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <strong>Manager:</strong> {formData.manager_name} (auto-assigned from TL)
              </div>
            )}
          </>
        )}

        {assignmentMode === 'manager_only' && (
          <div className="space-y-2">
            <Label>Manager *</Label>
            <Select value={formData.manager_id} onValueChange={handleManagerChange}>
              <SelectTrigger><SelectValue placeholder="Select Manager" /></SelectTrigger>
              <SelectContent>
                {managers.map(mgr => (
                  <SelectItem key={mgr.manager_id} value={mgr.manager_id}>{mgr.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {assignmentMode === 'fm_only' && (
          <div className="space-y-2">
            <Label>Fitness Manager *</Label>
            <Select value={formData.fm_id} onValueChange={handleFMChange}>
              <SelectTrigger><SelectValue placeholder="Select Fitness Manager" /></SelectTrigger>
              <SelectContent>
                {fitnessManagers.map(fm => (
                  <SelectItem key={fm.fm_id} value={fm.fm_id}>{fm.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {assignmentMode === 'admin_only' && (
          <div className="space-y-2">
            <Label>Admin *</Label>
            <Select value={formData.admin_id} onValueChange={handleAdminChange}>
              <SelectTrigger><SelectValue placeholder="Select Admin" /></SelectTrigger>
              <SelectContent>
                {users.filter(u => u.role === 'admin').map(admin => (
                  <SelectItem key={admin.user_id} value={admin.user_id}>{admin.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </>
      );
    };

    const handleSetPrimaryManager = async (gymId, managerId) => {
      try {
        await axiosInstance.put(`/gyms/${gymId}/primary-manager`, { manager_id: managerId });
        toast.success('Primary manager updated');
        fetchAllData();
      } catch (error) {
        const detail = error.response?.data?.detail;
        const errorMsg = typeof detail === 'string' ? detail : 'Failed to set primary manager';
        toast.error(errorMsg);
      }
    };

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Building2 size={20} /> Gyms ({displayedGyms.length} / {gyms.length})
            {unmappedGyms.length > 0 && (
              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">{unmappedGyms.length} unmapped</span>
            )}
          </h3>
          <div className="flex gap-2 items-center">
            <Input
              placeholder="Search gyms..."
              value={gymSearchText}
              onChange={(e) => setGymSearchText(e.target.value)}
              className="w-48"
            />
            <Select value={showUnmappedOnly ? "unmapped" : "all"} onValueChange={(v) => setShowUnmappedOnly(v === "unmapped")}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Show All Gyms</SelectItem>
                <SelectItem value="unmapped">Unmapped Only</SelectItem>
              </SelectContent>
            </Select>
            <Dialog open={assignOpen} onOpenChange={(v) => { setAssignOpen(v); if (!v) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="assign-gym-btn"><Plus size={16} className="mr-2" /> Assign Team to Gym</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Assign Team to Gym</DialogTitle></DialogHeader>
                <form onSubmit={handleAssignSubmit} className="space-y-4">
                  <AssignmentFields />
                  <Button type="submit" className="w-full"><Save size={16} className="mr-2" /> Assign</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) resetForm(); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Gym Assignment</DialogTitle></DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label>Gym ID</Label>
                <Input value={formData.gym_id} disabled className="bg-gray-100" />
              </div>
              <AssignmentFields />
              <Button type="submit" className="w-full"><Save size={16} className="mr-2" /> Save Changes</Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="data-table">
          <table>
            <thead>
              <tr><th>Gym ID</th><th>Name</th><th>Location</th><th>GPS</th><th>Assignment</th><th>Team Leader</th><th>Manager</th><th>Primary</th><th>Fitness Mgr</th><th>Admin</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {displayedGyms.map((gym, idx) => {
                const isUnmapped = !gym.assignment_mode || gym.assignment_mode === 'unassigned';
                const isPrimaryManager = gym.primary_manager_id && gym.primary_manager_id === gym.manager_id;
                return (
                  <tr key={idx} data-testid={`gym-row-${idx}`} className={isUnmapped ? 'bg-yellow-50' : ''}>
                    <td>{gym.gym_id}</td>
                    <td className="font-semibold">{gym.name}</td>
                    <td>{gym.location}</td>
                    <td>
                      <GPSBadge hasCoordinates={!!gym.coordinates} showSuccess={true} />
                    </td>
                    <td>
                      {isUnmapped ? (
                        <span className="status-badge error">Needs Assignment</span>
                      ) : (
                        <span className="status-badge success">
                          {gym.assignment_mode === 'tl_manager_fm' && 'TL+Mgr+FM'}
                          {gym.assignment_mode === 'tl_manager' && 'TL+Manager'}
                          {gym.assignment_mode === 'manager_only' && 'Manager Only'}
                          {gym.assignment_mode === 'fm_only' && 'FM Only'}
                          {gym.assignment_mode === 'admin_only' && 'Admin Only'}
                        </span>
                      )}
                    </td>
                    <td>{gym.tl_name || '-'}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        {gym.manager_name || '-'}
                        {isPrimaryManager && (
                          <Crown className="h-4 w-4 text-amber-500" title="Primary Manager" />
                        )}
                      </div>
                    </td>
                    <td>
                      {gym.manager_id ? (
                        isPrimaryManager ? (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                            <Crown className="h-3 w-3" /> Set
                          </span>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-6 px-2"
                            onClick={() => handleSetPrimaryManager(gym.gym_id, gym.manager_id)}
                            title="Set as primary manager for this gym"
                          >
                            Set Primary
                          </Button>
                        )
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td>{gym.fm_name || '-'}</td>
                    <td>{gym.admin_name || '-'}</td>
                    <td><span className={`status-badge ${gym.status === 'Active' ? 'success' : 'error'}`}>{gym.status}</span></td>
                    <td>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetLocation(gym)}
                          title="Set GPS Location"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <MapPin size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(gym)} title="Edit"><Edit size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(gym.gym_id)} title="Delete" className="text-red-600 hover:text-red-700"><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Location Dialog */}
        <LocationDialog
          open={locationDialogOpen}
          onOpenChange={setLocationDialogOpen}
          gym={locationGym}
          onSave={handleLocationSaved}
        />
      </div>
    );
  };

  // ============= FRONT DESK TAB =============
  const FrontDeskTab = () => {
    const [open, setOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [formData, setFormData] = useState({ fd_id: '', name: '', gym_id: '', gym_name: '', email: '', phone: '', status: 'Active' });

    const resetForm = () => setFormData({ fd_id: '', name: '', gym_id: '', gym_name: '', email: '', phone: '', status: 'Active' });

    const handleGymChange = (gymId) => {
      const gym = gyms.find(g => g.gym_id === gymId);
      setFormData({ ...formData, gym_id: gymId, gym_name: gym?.name || '' });
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.post('/front-desk', { ...formData, status: formData.status || 'Active' });
        toast.success('Front Desk added successfully');
        setOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to add front desk');
      }
    };

    const handleEdit = (item) => {
      setEditItem(item);
      setFormData({ ...item });
      setEditOpen(true);
    };

    const handleUpdate = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.put(`/front-desk/${editItem.fd_id}`, formData);
        toast.success('Front Desk updated successfully');
        setEditOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to update front desk');
      }
    };

    const handleDelete = async (id) => {
      if (window.confirm('Are you sure you want to delete this front desk staff?')) {
        try {
          await axiosInstance.delete(`/front-desk/${id}`);
          toast.success('Front Desk deleted');
          fetchAllData();
        } catch (error) {
          toast.error('Failed to delete front desk');
        }
      }
    };

    const FormFields = ({ isEdit = false }) => (
      <>
        <div className="grid grid-cols-2 gap-4">
          {isEdit ? (
            <div className="space-y-2">
              <Label>FD ID</Label>
              <Input value={formData.fd_id} disabled className="bg-gray-100" />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>FD ID</Label>
              <Input value="(Auto-generated)" disabled className="bg-gray-100 text-gray-500 italic" />
            </div>
          )}
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Assign to Gym *</Label>
          <Select value={formData.gym_id} onValueChange={handleGymChange}>
            <SelectTrigger><SelectValue placeholder="Select Gym" /></SelectTrigger>
            <SelectContent>
              {gyms.map(gym => (
                <SelectItem key={gym.gym_id} value={gym.gym_id}>{gym.name} ({gym.location})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Email</Label>
            <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Phone *</Label>
            <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
          </div>
        </div>
        {isEdit && (
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </>
    );

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Briefcase size={20} /> Front Desk Staff ({frontDesk.length})
          </h3>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-fd-btn"><Plus size={16} className="mr-2" /> Add Front Desk</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Front Desk Staff</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <FormFields />
                <Button type="submit" className="w-full"><Plus size={16} className="mr-2" /> Add Front Desk</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) resetForm(); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Front Desk Staff</DialogTitle></DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <FormFields isEdit />
              <Button type="submit" className="w-full"><Save size={16} className="mr-2" /> Save Changes</Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="data-table">
          <table>
            <thead>
              <tr><th>FD ID</th><th>Name</th><th>Gym</th><th>Email</th><th>Phone</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {frontDesk.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-8 text-gray-500">No front desk staff added yet</td></tr>
              ) : frontDesk.map((fd, idx) => (
                <tr key={idx} data-testid={`fd-row-${idx}`}>
                  <td>{fd.fd_id}</td>
                  <td className="font-semibold">{fd.name}</td>
                  <td>{fd.gym_name}</td>
                  <td>{fd.email || '-'}</td>
                  <td>{fd.phone}</td>
                  <td><span className={`status-badge ${fd.status === 'Active' ? 'success' : 'error'}`}>{fd.status}</span></td>
                  <td>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(fd)} title="Edit"><Edit size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(fd.fd_id)} title="Delete" className="text-red-600 hover:text-red-700"><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============= SOURCES TAB =============
  const SourcesTab = () => {
    const [open, setOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [formData, setFormData] = useState({ source_id: '', name: '', closure_type: '', needs_visit: '', description: '' });

    const resetForm = () => setFormData({ source_id: '', name: '', closure_type: '', needs_visit: '', description: '' });

    const sourceDescriptions = {
      'ODM Fresh': 'New leads from online digital marketing',
      'Reference': 'Referrals from existing members',
      'Direct Walk-in': 'Customers walking into the gym directly',
      'Unpaid (Expired)': 'Previously unpaid/expired memberships',
      'Renewal - Same Month': 'Members renewing in same month',
      'Renewal - Advanced': 'Members renewing before expiry'
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.post('/sources', { ...formData, gym_ids: gyms.map(g => g.gym_id) });
        toast.success('Source added successfully');
        setOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to add source');
      }
    };

    const handleEdit = (item) => {
      setEditItem(item);
      setFormData({ ...item });
      setEditOpen(true);
    };

    const handleUpdate = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.put(`/sources/${editItem.source_id}`, { ...formData, gym_ids: gyms.map(g => g.gym_id) });
        toast.success('Source updated successfully');
        setEditOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to update source');
      }
    };

    const handleDelete = async (id) => {
      if (window.confirm('Are you sure you want to delete this source?')) {
        try {
          await axiosInstance.delete(`/sources/${id}`);
          toast.success('Source deleted');
          fetchAllData();
        } catch (error) {
          toast.error('Failed to delete source');
        }
      }
    };

    const FormFields = ({ isEdit = false }) => (
      <>
        <div className="grid grid-cols-2 gap-4">
          {isEdit ? (
            <div className="space-y-2">
              <Label>Source ID</Label>
              <Input value={formData.source_id} disabled className="bg-gray-100" />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Source ID</Label>
              <Input value="(Auto-generated)" disabled className="bg-gray-100 text-gray-500 italic" />
            </div>
          )}
          <div className="space-y-2">
            <Label>Source Name *</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="ODM Fresh" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Closure Type *</Label>
            <Select value={formData.closure_type} onValueChange={(v) => setFormData({ ...formData, closure_type: v })}>
              <SelectTrigger><SelectValue placeholder="Select Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Counter">Counter</SelectItem>
                <SelectItem value="Phone">Phone</SelectItem>
                <SelectItem value="Both">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Visit Needed *</Label>
            <Select value={formData.needs_visit} onValueChange={(v) => setFormData({ ...formData, needs_visit: v })}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Description</Label>
          <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description of this source" />
        </div>
      </>
    );

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Target size={20} /> Sources ({sources.length})
          </h3>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-source-btn"><Plus size={16} className="mr-2" /> Add Source</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Source</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <FormFields />
                <Button type="submit" className="w-full"><Plus size={16} className="mr-2" /> Add Source</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) resetForm(); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Source</DialogTitle></DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <FormFields isEdit />
              <Button type="submit" className="w-full"><Save size={16} className="mr-2" /> Save Changes</Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="data-table">
          <table>
            <thead>
              <tr><th>Source ID</th><th>Source Name</th><th>Closure Type</th><th>Needs Visit?</th><th>Description</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {sources.map((src, idx) => (
                <tr key={idx} data-testid={`source-row-${idx}`}>
                  <td>{src.source_id}</td>
                  <td className="font-semibold">{src.name}</td>
                  <td><span className={`px-2 py-1 rounded text-xs font-medium ${src.closure_type === 'Both' ? 'bg-purple-100 text-purple-700' : src.closure_type === 'Counter' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{src.closure_type}</span></td>
                  <td><span className={`px-2 py-1 rounded text-xs font-medium ${src.needs_visit === 'Yes' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700'}`}>{src.needs_visit}</span></td>
                  <td className="text-gray-600 text-sm">{src.description || sourceDescriptions[src.name] || '-'}</td>
                  <td>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(src)} title="Edit"><Edit size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(src.source_id)} title="Delete" className="text-red-600 hover:text-red-700"><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============= PULSES TAB =============
  const PulsesTab = () => {
    const [open, setOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [formData, setFormData] = useState({ pulse_id: '', name: '', time_start: '', time_end: '', shift: '', entry_deadline: '' });

    const resetForm = () => setFormData({ pulse_id: '', name: '', time_start: '', time_end: '', shift: '', entry_deadline: '' });

    const getEntryDeadline = (endTime) => {
      if (!endTime) return '-';
      const [hours, minutes] = endTime.split(':').map(Number);
      const deadlineMinutes = minutes + 30;
      const deadlineHours = hours + Math.floor(deadlineMinutes / 60);
      const finalMinutes = deadlineMinutes % 60;
      return `${String(deadlineHours % 24).padStart(2, '0')}:${String(finalMinutes).padStart(2, '0')}`;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        const deadline = formData.entry_deadline || getEntryDeadline(formData.time_end);
        await axiosInstance.post('/pulses', { ...formData, entry_deadline: deadline });
        toast.success('Pulse added successfully');
        setOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to add pulse');
      }
    };

    const handleEdit = (item) => {
      setEditItem(item);
      setFormData({ ...item });
      setEditOpen(true);
    };

    const handleUpdate = async (e) => {
      e.preventDefault();
      try {
        const deadline = formData.entry_deadline || getEntryDeadline(formData.time_end);
        await axiosInstance.put(`/pulses/${editItem.pulse_id}`, { ...formData, entry_deadline: deadline });
        toast.success('Pulse updated successfully');
        setEditOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to update pulse');
      }
    };

    const handleDelete = async (id) => {
      if (window.confirm('Are you sure you want to delete this pulse?')) {
        try {
          await axiosInstance.delete(`/pulses/${id}`);
          toast.success('Pulse deleted');
          fetchAllData();
        } catch (error) {
          toast.error('Failed to delete pulse');
        }
      }
    };

    const FormFields = ({ isEdit = false }) => (
      <>
        <div className="grid grid-cols-2 gap-4">
          {isEdit ? (
            <div className="space-y-2">
              <Label>Pulse ID</Label>
              <Input value={formData.pulse_id} disabled className="bg-gray-100" />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Pulse ID</Label>
              <Input value="(Auto-generated)" disabled className="bg-gray-100 text-gray-500 italic" />
            </div>
          )}
          <div className="space-y-2">
            <Label>Pulse Name *</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="Pulse 1" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Start Time *</Label>
            <Input type="time" value={formData.time_start} onChange={(e) => setFormData({ ...formData, time_start: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label>End Time *</Label>
            <Input type="time" value={formData.time_end} onChange={(e) => setFormData({ ...formData, time_end: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label>Entry Deadline</Label>
            <Input type="time" value={formData.entry_deadline} onChange={(e) => setFormData({ ...formData, entry_deadline: e.target.value })} placeholder="Auto: End+30min" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Shift *</Label>
          <Select value={formData.shift} onValueChange={(v) => setFormData({ ...formData, shift: v })}>
            <SelectTrigger><SelectValue placeholder="Select Shift" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Shift 1">Shift 1 (Morning)</SelectItem>
              <SelectItem value="Overlap">Overlap</SelectItem>
              <SelectItem value="Shift 2">Shift 2 (Evening)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </>
    );

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Clock size={20} /> Pulses (Time Windows) ({pulses.length})
          </h3>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-pulse-btn"><Plus size={16} className="mr-2" /> Add Pulse</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Pulse</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <FormFields />
                <Button type="submit" className="w-full"><Plus size={16} className="mr-2" /> Add Pulse</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) resetForm(); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Pulse</DialogTitle></DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <FormFields isEdit />
              <Button type="submit" className="w-full"><Save size={16} className="mr-2" /> Save Changes</Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="data-table">
          <table>
            <thead>
              <tr><th>Pulse ID</th><th>Name</th><th>Start Time</th><th>End Time</th><th>Shift</th><th>Entry Deadline</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {pulses.map((pulse, idx) => (
                <tr key={idx} data-testid={`pulse-row-${idx}`}>
                  <td>{pulse.pulse_id}</td>
                  <td className="font-semibold">{pulse.name}</td>
                  <td>{pulse.time_start}</td>
                  <td>{pulse.time_end}</td>
                  <td><span className={`px-2 py-1 rounded text-xs font-medium ${pulse.shift === 'Shift 1' ? 'bg-yellow-100 text-yellow-700' : pulse.shift === 'Shift 2' ? 'bg-indigo-100 text-indigo-700' : 'bg-green-100 text-green-700'}`}>{pulse.shift}</span></td>
                  <td className="font-medium text-red-600">{pulse.entry_deadline || getEntryDeadline(pulse.time_end)}</td>
                  <td>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(pulse)} title="Edit"><Edit size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(pulse.pulse_id)} title="Delete" className="text-red-600 hover:text-red-700"><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============= BENCHMARKS TAB =============
  const BenchmarksTab = () => {
    const [open, setOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [formData, setFormData] = useState({ metric: '', target: '', warning_below: '', red_below: '' });

    const resetForm = () => setFormData({ metric: '', target: '', warning_below: '', red_below: '' });

    const metricMeanings = {
      'Connect Rate': 'Percentage of dialed calls that were answered',
      'Schedule Rate': 'Percentage of connected calls that led to visit scheduling',
      'Visit Done Rate': 'Percentage of scheduled visits that actually happened',
      'Closure Rate': 'Percentage of expected closures achieved'
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.post('/benchmarks', { 
          metric: formData.metric, 
          target: parseFloat(formData.target), 
          warning_below: parseFloat(formData.warning_below) || parseFloat(formData.target) - 5,
          red_below: parseFloat(formData.red_below) 
        });
        toast.success('Benchmark added successfully');
        setOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to add benchmark');
      }
    };

    const handleEdit = (item) => {
      setEditItem(item);
      setFormData({ 
        metric: item.metric, 
        target: item.target?.toString() || '', 
        warning_below: item.warning_below?.toString() || '', 
        red_below: item.red_below?.toString() || '' 
      });
      setEditOpen(true);
    };

    const handleUpdate = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.put(`/benchmarks/${encodeURIComponent(editItem.metric)}`, { 
          metric: formData.metric, 
          target: parseFloat(formData.target), 
          warning_below: parseFloat(formData.warning_below) || parseFloat(formData.target) - 5,
          red_below: parseFloat(formData.red_below) 
        });
        toast.success('Benchmark updated successfully');
        setEditOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to update benchmark');
      }
    };

    const handleDelete = async (metric) => {
      if (window.confirm('Are you sure you want to delete this benchmark?')) {
        try {
          await axiosInstance.delete(`/benchmarks/${encodeURIComponent(metric)}`);
          toast.success('Benchmark deleted');
          fetchAllData();
        } catch (error) {
          toast.error('Failed to delete benchmark');
        }
      }
    };

    const FormFields = ({ isEdit = false }) => (
      <>
        <div className="space-y-2">
          <Label>Metric Name *</Label>
          <Select value={formData.metric} onValueChange={(v) => setFormData({ ...formData, metric: v })} disabled={isEdit}>
            <SelectTrigger><SelectValue placeholder="Select Metric" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Connect Rate">Connect Rate</SelectItem>
              <SelectItem value="Schedule Rate">Schedule Rate</SelectItem>
              <SelectItem value="Visit Done Rate">Visit Done Rate</SelectItem>
              <SelectItem value="Closure Rate">Closure Rate</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Target (%) *</Label>
            <Input type="number" step="0.1" value={formData.target} onChange={(e) => setFormData({ ...formData, target: e.target.value })} required placeholder="70" />
          </div>
          <div className="space-y-2">
            <Label>Warning Below (%)</Label>
            <Input type="number" step="0.1" value={formData.warning_below} onChange={(e) => setFormData({ ...formData, warning_below: e.target.value })} placeholder="65" />
          </div>
          <div className="space-y-2">
            <Label>Critical Below (%) *</Label>
            <Input type="number" step="0.1" value={formData.red_below} onChange={(e) => setFormData({ ...formData, red_below: e.target.value })} required placeholder="60" />
          </div>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
          <strong>Note:</strong> Values below Warning = 🟡 Yellow | Below Critical = 🔴 Red
        </div>
      </>
    );

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 size={20} /> Benchmarks (Target Percentages) ({benchmarks.length})
          </h3>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-benchmark-btn"><Plus size={16} className="mr-2" /> Add Benchmark</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Benchmark Metric</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <FormFields />
                <Button type="submit" className="w-full"><Plus size={16} className="mr-2" /> Add Benchmark</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) resetForm(); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Benchmark</DialogTitle></DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <FormFields isEdit />
              <Button type="submit" className="w-full"><Save size={16} className="mr-2" /> Save Changes</Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="data-table">
          <table>
            <thead>
              <tr><th>Metric</th><th>Target (%)</th><th>Warning Below (%)</th><th>Critical Below (%)</th><th>Meaning</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {benchmarks.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-8 text-gray-500">No benchmarks configured</td></tr>
              ) : benchmarks.map((bm, idx) => (
                <tr key={idx} data-testid={`benchmark-row-${idx}`}>
                  <td className="font-semibold">{bm.metric}</td>
                  <td><span className="text-green-600 font-medium">{bm.target}%</span></td>
                  <td><span className="text-yellow-600 font-medium">{bm.warning_below || (bm.target - 5)}%</span></td>
                  <td><span className="text-red-600 font-medium">{bm.red_below}%</span></td>
                  <td className="text-gray-600 text-sm">{metricMeanings[bm.metric] || '-'}</td>
                  <td>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(bm)} title="Edit"><Edit size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(bm.metric)} title="Delete" className="text-red-600 hover:text-red-700"><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============= TARGETS TAB =============
  const TargetsTab = () => {
    const [open, setOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [formData, setFormData] = useState({ metric: '', target: '' });

    const resetForm = () => setFormData({ metric: '', target: '' });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.post('/targets', { metric: formData.metric, target: parseInt(formData.target) });
        toast.success('Target added successfully');
        setOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to add target');
      }
    };

    const handleEdit = (item) => {
      setEditItem(item);
      setFormData({ metric: item.metric, target: item.target?.toString() || '' });
      setEditOpen(true);
    };

    const handleUpdate = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.put(`/targets/${encodeURIComponent(editItem.metric)}`, { metric: formData.metric, target: parseInt(formData.target) });
        toast.success('Target updated successfully');
        setEditOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error('Failed to update target');
      }
    };

    const handleDelete = async (metric) => {
      if (window.confirm('Are you sure you want to delete this target?')) {
        try {
          await axiosInstance.delete(`/targets/${encodeURIComponent(metric)}`);
          toast.success('Target deleted');
          fetchAllData();
        } catch (error) {
          toast.error('Failed to delete target');
        }
      }
    };

    const FormFields = ({ isEdit = false }) => (
      <>
        <div className="space-y-2">
          <Label>Metric *</Label>
          <Select value={formData.metric} onValueChange={(v) => setFormData({ ...formData, metric: v })} disabled={isEdit}>
            <SelectTrigger><SelectValue placeholder="Select Metric" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Dialed">Dialed</SelectItem>
              <SelectItem value="Connected">Connected</SelectItem>
              <SelectItem value="Visit Scheduled">Visit Scheduled</SelectItem>
              <SelectItem value="Visit Done">Visit Done</SelectItem>
              <SelectItem value="Closure Expected">Closure Expected</SelectItem>
              <SelectItem value="Closure Done">Closure Done</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Target Value *</Label>
          <Input type="number" value={formData.target} onChange={(e) => setFormData({ ...formData, target: e.target.value })} required placeholder="80" />
        </div>
      </>
    );

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Target size={20} /> Daily Targets ({targets.length})
          </h3>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-target-btn"><Plus size={16} className="mr-2" /> Add Target</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Daily Target</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <FormFields />
                <Button type="submit" className="w-full"><Plus size={16} className="mr-2" /> Add Target</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) resetForm(); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Daily Target</DialogTitle></DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <FormFields isEdit />
              <Button type="submit" className="w-full"><Save size={16} className="mr-2" /> Save Changes</Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="data-table">
          <table>
            <thead>
              <tr><th>Metric</th><th>Daily Target</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {targets.length === 0 ? (
                <tr><td colSpan="3" className="text-center py-8 text-gray-500">No targets configured</td></tr>
              ) : targets.map((tgt, idx) => (
                <tr key={idx} data-testid={`target-row-${idx}`}>
                  <td className="font-semibold">{tgt.metric}</td>
                  <td><span className="text-blue-600 font-medium">{tgt.target}</span></td>
                  <td>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(tgt)} title="Edit"><Edit size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(tgt.metric)} title="Delete" className="text-red-600 hover:text-red-700"><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============= USERS TAB =============
  const UsersTab = () => {
    const [open, setOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [resetItem, setResetItem] = useState(null);
    const [formData, setFormData] = useState({ email: '', name: '', password: '', role: 'team_leader', linked_id: '' });
    const [newPassword, setNewPassword] = useState('');

    const resetForm = () => setFormData({ email: '', name: '', password: '', role: 'team_leader', linked_id: '' });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.post('/auth/register', formData);
        toast.success('User created successfully');
        setOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create user');
      }
    };

    const handleEdit = (item) => {
      setEditItem(item);
      setFormData({ email: item.email, name: item.name, role: item.role, linked_id: item.linked_id || '', status: item.status });
      setEditOpen(true);
    };

    const handleUpdate = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.put(`/users/${editItem.id}`, {
          email: formData.email,
          name: formData.name,
          role: formData.role,
          linked_id: formData.linked_id || null,
          status: formData.status
        });
        toast.success('User updated successfully');
        setEditOpen(false);
        fetchAllData();
        resetForm();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update user');
      }
    };

    const handleDelete = async (id) => {
      if (window.confirm('Are you sure you want to delete this user?')) {
        try {
          await axiosInstance.delete(`/users/${id}`);
          toast.success('User deleted');
          fetchAllData();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to delete user');
        }
      }
    };

    const handleResetPassword = async (e) => {
      e.preventDefault();
      try {
        await axiosInstance.post(`/users/${resetItem.id}/reset-password`, { new_password: newPassword });
        toast.success('Password reset successfully');
        setResetOpen(false);
        setNewPassword('');
        setResetItem(null);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to reset password');
      }
    };

    const roleOptions = [
      { value: 'admin', label: 'Admin' },
      { value: 'manager', label: 'Manager' },
      { value: 'team_leader', label: 'Team Leader' }
    ];

    const getRoleBadgeColor = (role) => {
      switch (role) {
        case 'admin': return 'bg-red-100 text-red-800';
        case 'manager': return 'bg-blue-100 text-blue-800';
        case 'team_leader': return 'bg-green-100 text-green-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    };

    // If users is empty, admin-only feature message
    if (users.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          User management is admin-only. No users available or you don't have permission.
        </div>
      );
    }

    return (
      <div data-testid="users-tab-content">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Users ({users.length})</h3>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-user-btn"><Plus size={16} className="mr-1" /> Add User</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><Label>Email</Label><Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required /></div>
                <div><Label>Name</Label><Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required /></div>
                <div><Label>Password</Label><Input type="password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} required /></div>
                <div>
                  <Label>Role</Label>
                  <Select value={formData.role} onValueChange={v => setFormData({ ...formData, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {roleOptions.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {formData.role !== 'admin' && (
                  <div>
                    <Label>Linked ID (Manager/TL ID)</Label>
                    <Input value={formData.linked_id} onChange={e => setFormData({ ...formData, linked_id: e.target.value })} placeholder="e.g., MGR001 or TL001" />
                  </div>
                )}
                <Button type="submit" className="w-full">Create User</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div><Label>Email</Label><Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required /></div>
              <div><Label>Name</Label><Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required /></div>
              <div>
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={v => setFormData({ ...formData, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roleOptions.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={v => setFormData({ ...formData, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.role !== 'admin' && (
                <div>
                  <Label>Linked ID</Label>
                  <Input value={formData.linked_id} onChange={e => setFormData({ ...formData, linked_id: e.target.value })} />
                </div>
              )}
              <Button type="submit" className="w-full">Update User</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetOpen} onOpenChange={setResetOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Reset Password for {resetItem?.name}</DialogTitle></DialogHeader>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div><Label>New Password</Label><Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={6} /></div>
              <Button type="submit" className="w-full">Reset Password</Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, idx) => (
                <tr key={idx} data-testid={`user-row-${idx}`}>
                  <td>{user.email}</td>
                  <td>{user.name}</td>
                  <td><span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>{user.role}</span></td>
                  <td><span className={`px-2 py-1 rounded-full text-xs ${user.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{user.status}</span></td>
                  <td>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(user)} title="Edit"><Edit size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => { setResetItem(user); setResetOpen(true); }} title="Reset Password" className="text-yellow-600 hover:text-yellow-700">Reset</Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(user.id)} title="Delete" className="text-red-600 hover:text-red-700"><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============= GYM SYNC TAB =============
  const GymSyncTab = () => {
    const [syncHistory, setSyncHistory] = useState([]);
    const [syncStatus, setSyncStatus] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(true);

    useEffect(() => {
      fetchSyncData();
    }, []);

    const fetchSyncData = async () => {
      try {
        const [history, status] = await Promise.all([
          axiosInstance.get('/admin/sync-history'),
          axiosInstance.get('/admin/sync-status')
        ]);
        setSyncHistory(history.data.sync_logs || []);
        setSyncStatus(status.data);
      } catch (error) {
        if (error.response?.status !== 403) {
          toast.error('Failed to load sync data');
        }
      } finally {
        setLoadingHistory(false);
      }
    };

    const triggerSync = async () => {
      setSyncing(true);
      try {
        const result = await axiosInstance.post('/admin/sync-gyms');
        toast.success(`Sync completed: ${result.data.created} created, ${result.data.updated} updated`);
        fetchSyncData();
        fetchAllData(); // Refresh gyms list
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Sync failed');
      } finally {
        setSyncing(false);
      }
    };

    const getStatusIcon = (status) => {
      switch (status) {
        case 'success': return <CheckCircle size={16} className="text-green-600" />;
        case 'partial_success': return <AlertCircle size={16} className="text-yellow-600" />;
        case 'failed': return <XCircle size={16} className="text-red-600" />;
        default: return <AlertCircle size={16} className="text-gray-600" />;
      }
    };

    const formatDate = (isoString) => {
      if (!isoString) return 'N/A';
      const date = new Date(isoString);
      return date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        dateStyle: 'medium',
        timeStyle: 'short'
      });
    };

    if (loadingHistory) {
      return <div className="flex items-center justify-center h-64"><div className="loading-spinner" /></div>;
    }

    return (
      <div data-testid="gym-sync-tab-content">
        <div className="space-y-6">
          {/* Sync Status Card */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">External Gym Sync</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Automatically syncs active gyms from api.wtfgyms.com daily at 12:00 IST
                </p>
                {syncStatus?.last_sync && (
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-700">Last Sync:</span>
                      <span className="text-gray-600">{formatDate(syncStatus.last_sync.completed_at)}</span>
                      {getStatusIcon(syncStatus.last_sync.status)}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-700">Results:</span>
                      <span className="text-green-600">{syncStatus.last_sync.created} created</span>
                      <span className="text-blue-600">{syncStatus.last_sync.updated} updated</span>
                      {syncStatus.last_sync.skipped > 0 && (
                        <span className="text-yellow-600">{syncStatus.last_sync.skipped} skipped</span>
                      )}
                    </div>
                  </div>
                )}
                {!syncStatus?.api_configured && (
                  <div className="mt-2 text-sm text-yellow-700 bg-yellow-100 px-3 py-2 rounded">
                    ⚠️ API token not configured - sync disabled
                  </div>
                )}
              </div>
              <Button
                onClick={triggerSync}
                disabled={syncing || !syncStatus?.api_configured}
                size="sm"
                className="flex items-center gap-2"
              >
                <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
                {syncing ? 'Syncing...' : 'Sync Now'}
              </Button>
            </div>
          </div>

          {/* Sync History Table */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Sync History ({syncHistory.length})</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Started At</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Fetched</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th>Skipped</th>
                    <th>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {syncHistory.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center text-gray-500 py-8">
                        No sync history yet. Click "Sync Now" to run the first sync.
                      </td>
                    </tr>
                  ) : (
                    syncHistory.map((log, idx) => (
                      <tr key={log.sync_id || idx}>
                        <td>{formatDate(log.started_at)}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(log.status)}
                            <span className={
                              log.status === 'success' ? 'text-green-700 font-medium' :
                              log.status === 'partial_success' ? 'text-yellow-700 font-medium' :
                              'text-red-700 font-medium'
                            }>
                              {log.status === 'partial_success' ? 'Partial' : log.status}
                            </span>
                          </div>
                        </td>
                        <td>{log.duration_seconds?.toFixed(1)}s</td>
                        <td>{log.total_fetched || 0}</td>
                        <td className="text-green-600 font-medium">{log.created || 0}</td>
                        <td className="text-blue-600 font-medium">{log.updated || 0}</td>
                        <td className={log.skipped > 0 ? 'text-yellow-600' : ''}>{log.skipped || 0}</td>
                        <td>
                          {log.error_count > 0 ? (
                            <span className="text-red-600 font-medium" title={log.errors?.join(', ')}>
                              {log.error_count}
                            </span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============= HQ SETTINGS TAB =============
  const HQSettingsTab = () => {
    const [hqData, setHqData] = useState(null);
    const [url, setUrl] = useState('');
    const [coordinates, setCoordinates] = useState(null);
    const [error, setError] = useState('');
    const [name, setName] = useState('WTF HQ');
    const [radius, setRadius] = useState(100);
    const [saving, setSaving] = useState(false);
    const [loadingHQ, setLoadingHQ] = useState(true);

    // Fetch current HQ settings on mount
    useEffect(() => {
      const fetchHQSettings = async () => {
        try {
          const response = await axiosInstance.get('/master-config/hq');
          setHqData(response.data);
          setName(response.data.name || 'WTF HQ');
          setRadius(response.data.geofence_radius || 100);
          if (response.data.google_maps_url) {
            setUrl(response.data.google_maps_url);
          }
          if (response.data.coordinates) {
            setCoordinates(response.data.coordinates);
          }
        } catch (err) {
          // If not admin or endpoint fails, show error
          if (err.response?.status === 403) {
            setError('Admin access required to view HQ settings');
          }
        } finally {
          setLoadingHQ(false);
        }
      };
      fetchHQSettings();
    }, []);

    // Extract coordinates when URL changes
    useEffect(() => {
      if (!url) {
        // Don't clear coordinates if we just loaded from server
        if (!hqData?.google_maps_url) {
          setCoordinates(null);
        }
        setError('');
        return;
      }

      // Check for short URLs
      if (isShortUrl(url)) {
        setError('Short URLs (goo.gl, maps.app) are not supported. Please use "Copy Link" from Google Maps.');
        return;
      }

      const coords = extractCoordinates(url);
      if (coords && validateCoordinates(coords)) {
        setCoordinates(coords);
        setError('');
      } else if (url !== hqData?.google_maps_url) {
        setError('Could not extract coordinates. Please paste a valid Google Maps link.');
      }
    }, [url, hqData]);

    const handleSave = async () => {
      if (!coordinates) {
        toast.error('Please enter a valid Google Maps URL first');
        return;
      }

      setSaving(true);
      try {
        await axiosInstance.put('/master-config/hq', {
          name: name,
          google_maps_url: url,
          coordinates: coordinates,
          geofence_radius: radius
        });
        toast.success('HQ location saved successfully');
        setHqData({
          name,
          google_maps_url: url,
          coordinates,
          geofence_radius: radius
        });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to save HQ settings');
      } finally {
        setSaving(false);
      }
    };

    if (loadingHQ) {
      return (
        <div className="flex items-center justify-center h-48">
          <div className="loading-spinner" />
        </div>
      );
    }

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Settings size={20} /> HQ Settings
          </h3>
        </div>

        <div className="max-w-2xl space-y-6">
          {/* HQ Name */}
          <div className="space-y-2">
            <Label>HQ Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="WTF HQ"
            />
          </div>

          {/* Google Maps URL */}
          <div className="space-y-2">
            <Label>HQ Location (Google Maps URL)</Label>
            <Input
              placeholder="https://www.google.com/maps/@..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className={error ? 'border-red-300' : coordinates ? 'border-green-300' : ''}
            />
            <p className="text-xs text-gray-500">
              Open HQ in Google Maps, click Share, then Copy Link. <strong>Note:</strong> Short URLs (maps.app.goo.gl) are not supported - use the full URL.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Extracted Coordinates */}
          {coordinates && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <CheckCircle size={14} className="text-green-600" />
                Extracted Coordinates
              </Label>
              <div className="text-sm font-mono bg-green-50 text-green-800 p-3 rounded-lg border border-green-200">
                {formatCoordinates(coordinates)}
              </div>
            </div>
          )}

          {/* Map Preview */}
          <div className="space-y-2">
            <Label>Map Preview</Label>
            <MapPreview coordinates={coordinates} height={250} />
          </div>

          {/* Geofence Radius */}
          <div className="space-y-2">
            <Label>Geofence Radius (meters)</Label>
            <Input
              type="number"
              min={10}
              max={1000}
              value={radius}
              onChange={(e) => setRadius(parseInt(e.target.value) || 100)}
            />
            <p className="text-xs text-gray-500">
              Staff must be within this distance from HQ to check in. Default: 100m, Range: 10-1000m.
            </p>
          </div>

          {/* Save Button */}
          <div className="pt-4">
            <Button
              onClick={handleSave}
              disabled={!coordinates || saving}
              className="bg-maroon-600 hover:bg-maroon-700"
            >
              {saving ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Save HQ Settings
                </>
              )}
            </Button>
          </div>

          {/* Current Settings Info */}
          {hqData?.coordinates && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
              <h4 className="text-sm font-medium mb-2">Current Saved Settings</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Name:</strong> {hqData.name}</p>
                <p><strong>Coordinates:</strong> {formatCoordinates(hqData.coordinates)}</p>
                <p><strong>Geofence Radius:</strong> {hqData.geofence_radius}m</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============= TEAM HIERARCHY TAB =============
  const TeamHierarchyTab = () => {
    const [expanded, setExpanded] = useState({ admins: true });

    useEffect(() => {
      if (!hierarchyData && !hierarchyLoading) {
        fetchHierarchy();
      }
    }, []);

    const fetchHierarchy = async () => {
      setHierarchyLoading(true);
      try {
        const res = await axiosInstance.get('/wtf-data/team-hierarchy');
        setHierarchyData(res.data);
      } catch (error) {
        toast.error('Failed to load team hierarchy');
      } finally {
        setHierarchyLoading(false);
      }
    };

    const toggle = (key) => {
      setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const isOpen = (key) => !!expanded[key];

    const ExpandIcon = ({ nodeKey }) => {
      const open = isOpen(nodeKey);
      return open
        ? <ChevronDown size={16} className="text-gray-400 shrink-0 cursor-pointer" onClick={(e) => { e.stopPropagation(); toggle(nodeKey); }} />
        : <ChevronRight size={16} className="text-gray-400 shrink-0 cursor-pointer" onClick={(e) => { e.stopPropagation(); toggle(nodeKey); }} />;
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return null;
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      } catch {
        return dateStr;
      }
    };

    // Count helpers
    const countTLGyms = (tl) => (tl.gyms || []).length;
    const countTLStaff = (tl) => (tl.gyms || []).reduce((sum, g) => sum + (g.staff || []).length, 0);
    const countManagerTLs = (mgr) => (mgr.team_leaders || []).length;
    const countManagerGyms = (mgr) => (mgr.team_leaders || []).reduce((sum, tl) => sum + countTLGyms(tl), 0);
    const countManagerStaff = (mgr) => (mgr.team_leaders || []).reduce((sum, tl) => sum + countTLStaff(tl), 0);
    const countManagerFMs = (mgr) => (mgr.fitness_managers || []).length;

    if (hierarchyLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <RefreshCw size={24} className="animate-spin text-gray-400" />
          <span className="ml-3 text-gray-500">Loading team hierarchy...</span>
        </div>
      );
    }

    if (!hierarchyData) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <Users size={48} className="mb-4 text-gray-300" />
          <p>No hierarchy data available</p>
          <Button variant="outline" className="mt-4" onClick={fetchHierarchy}>
            <RefreshCw size={16} className="mr-2" /> Load Hierarchy
          </Button>
        </div>
      );
    }

    const { hierarchy = [], managers: mgrList = [], unassigned_tls = [] } = hierarchyData;

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Crown size={20} className="text-amber-500" /> Team Hierarchy
          </h3>
          <Button variant="outline" size="sm" onClick={fetchHierarchy} disabled={hierarchyLoading}>
            <RefreshCw size={14} className={`mr-2 ${hierarchyLoading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-amber-700">{hierarchy.length}</div>
            <div className="text-xs text-amber-600">Admins</div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-purple-700">{mgrList.length}</div>
            <div className="text-xs text-purple-600">Managers</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-700">{mgrList.reduce((s, m) => s + countManagerTLs(m), 0) + unassigned_tls.length}</div>
            <div className="text-xs text-blue-600">Team Leaders</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-700">{mgrList.reduce((s, m) => s + countManagerGyms(m), 0)}</div>
            <div className="text-xs text-green-600">Gyms</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-orange-700">{mgrList.reduce((s, m) => s + countManagerFMs(m), 0)}</div>
            <div className="text-xs text-orange-600">Fitness Mgrs</div>
          </div>
        </div>

        {/* Tree view */}
        <div className="border rounded-lg overflow-hidden">

          {/* Admins section */}
          <div className="bg-amber-50 border-b">
            <div
              className="flex items-center gap-2 p-3 cursor-pointer hover:bg-amber-100 transition-colors"
              onClick={() => toggle('admins')}
            >
              <ExpandIcon nodeKey="admins" />
              <Crown size={18} className="text-amber-600" />
              <span className="font-semibold text-amber-800">ADMINS</span>
              <span className="text-xs text-amber-500 ml-1">({hierarchy.length})</span>
            </div>
            {isOpen('admins') && (
              <div className="pb-2">
                {hierarchy.map((admin, idx) => (
                  <div key={idx} className="flex items-center gap-2 pl-10 pr-3 py-1.5 text-sm">
                    <Crown size={14} className="text-amber-400 shrink-0" />
                    <span className="font-medium">{admin.name}</span>
                    {admin.email && (
                      <a href={`mailto:${admin.email}`} className="text-blue-600 hover:underline text-xs">{admin.email}</a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Managers section */}
          {mgrList.map((mgr, mgrIdx) => {
            const mgrKey = `mgr-${mgrIdx}`;
            const tlCount = countManagerTLs(mgr);
            const gymCount = countManagerGyms(mgr);
            const staffCount = countManagerStaff(mgr);
            const fmCount = countManagerFMs(mgr);

            return (
              <div key={mgrIdx} className="border-b last:border-b-0">
                {/* Manager row */}
                <div
                  className="flex items-center gap-2 p-3 cursor-pointer hover:bg-purple-50 transition-colors bg-white"
                  onClick={() => toggle(mgrKey)}
                >
                  <ExpandIcon nodeKey={mgrKey} />
                  <Users size={18} className="text-purple-600 shrink-0" />
                  <span className="font-semibold text-purple-800">MANAGER: {mgr.name}</span>
                  {mgr.email && (
                    <a href={`mailto:${mgr.email}`} className="text-blue-600 hover:underline text-xs ml-1" onClick={(e) => e.stopPropagation()}>
                      ({mgr.email})
                    </a>
                  )}
                  <div className="ml-auto flex gap-2 text-xs text-gray-500">
                    {tlCount > 0 && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{tlCount} TLs</span>}
                    {gymCount > 0 && <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded">{gymCount} gyms</span>}
                    {staffCount > 0 && <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{staffCount} FDs</span>}
                    {fmCount > 0 && <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">{fmCount} FMs</span>}
                  </div>
                </div>

                {isOpen(mgrKey) && (
                  <div className="border-l-2 border-purple-200 ml-5">

                    {/* Team Leaders under this manager */}
                    {(mgr.team_leaders || []).map((tl, tlIdx) => {
                      const tlKey = `${mgrKey}-tl-${tlIdx}`;
                      const tlGymCount = countTLGyms(tl);
                      const tlStaffCount = countTLStaff(tl);

                      return (
                        <div key={tlIdx}>
                          {/* TL row */}
                          <div
                            className="flex items-center gap-2 pl-4 pr-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors"
                            onClick={() => toggle(tlKey)}
                          >
                            <ExpandIcon nodeKey={tlKey} />
                            <UserCheck size={16} className="text-blue-600 shrink-0" />
                            <span className="font-medium text-blue-800 text-sm">TL: {tl.name}</span>
                            {tl.email && (
                              <a href={`mailto:${tl.email}`} className="text-blue-500 hover:underline text-xs ml-1" onClick={(e) => e.stopPropagation()}>
                                ({tl.email})
                              </a>
                            )}
                            {tl.linked_id && <span className="text-xs text-gray-400 ml-1">[{tl.linked_id}]</span>}
                            <div className="ml-auto flex gap-2 text-xs text-gray-500">
                              {tlGymCount > 0 && <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded">{tlGymCount} gyms</span>}
                              {tlStaffCount > 0 && <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{tlStaffCount} FDs</span>}
                            </div>
                          </div>

                          {isOpen(tlKey) && (
                            <div className="border-l-2 border-blue-200 ml-8">

                              {/* Gyms under this TL */}
                              {(tl.gyms || []).map((gym, gymIdx) => {
                                const gymKey = `${tlKey}-gym-${gymIdx}`;
                                const fdCount = (gym.staff || []).length;

                                return (
                                  <div key={gymIdx}>
                                    {/* Gym row */}
                                    <div
                                      className="flex items-center gap-2 pl-4 pr-3 py-2 cursor-pointer hover:bg-green-50 transition-colors"
                                      onClick={() => toggle(gymKey)}
                                    >
                                      <ExpandIcon nodeKey={gymKey} />
                                      <Building2 size={16} className="text-green-600 shrink-0" />
                                      <span className="font-medium text-green-800 text-sm">{gym.gym_name}</span>
                                      {fdCount > 0 && (
                                        <span className="text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded ml-auto">{fdCount} staff</span>
                                      )}
                                    </div>

                                    {isOpen(gymKey) && (gym.staff || []).length > 0 && (
                                      <div className="border-l-2 border-green-200 ml-8">
                                        {gym.staff.map((fd, fdIdx) => (
                                          <div key={fdIdx} className="flex items-center gap-2 pl-4 pr-3 py-1.5 text-sm hover:bg-gray-50 transition-colors">
                                            <Briefcase size={14} className="text-slate-500 shrink-0" />
                                            <span className="font-medium text-slate-700">{fd.name}</span>
                                            {fd.mobile && (
                                              <a href={`tel:${fd.mobile}`} className="text-blue-600 hover:underline text-xs">{fd.mobile}</a>
                                            )}
                                            {fd.email && (
                                              <a href={`mailto:${fd.email}`} className="text-blue-500 hover:underline text-xs">{fd.email}</a>
                                            )}
                                            {fd.joining_date && (
                                              <span className="text-xs text-gray-400 ml-auto">Joined: {formatDate(fd.joining_date)}</span>
                                            )}
                                            {fd.status && (
                                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                                fd.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                              }`}>{fd.status}</span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {isOpen(gymKey) && (gym.staff || []).length === 0 && (
                                      <div className="pl-12 pr-3 py-1.5 text-xs text-gray-400 italic">No front desk staff assigned</div>
                                    )}
                                  </div>
                                );
                              })}

                              {(tl.gyms || []).length === 0 && (
                                <div className="pl-4 pr-3 py-1.5 text-xs text-gray-400 italic">No gyms assigned</div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Fitness Managers under this manager */}
                    {(mgr.fitness_managers || []).length > 0 && (
                      <div className="border-t border-dashed border-gray-200 mt-1">
                        {mgr.fitness_managers.map((fm, fmIdx) => (
                          <div key={fmIdx} className="flex items-center gap-2 pl-4 pr-3 py-2 text-sm hover:bg-orange-50 transition-colors">
                            <Dumbbell size={16} className="text-orange-500 shrink-0" />
                            <span className="font-medium text-orange-800">FM: {fm.name}</span>
                            {fm.email && (
                              <a href={`mailto:${fm.email}`} className="text-blue-500 hover:underline text-xs ml-1">{fm.email}</a>
                            )}
                            {fm.linked_id && <span className="text-xs text-gray-400 ml-1">[{fm.linked_id}]</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    {(mgr.team_leaders || []).length === 0 && (mgr.fitness_managers || []).length === 0 && (
                      <div className="pl-4 pr-3 py-2 text-xs text-gray-400 italic">No team leaders or fitness managers assigned</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Unassigned TLs section */}
          {unassigned_tls.length > 0 && (
            <div className="bg-yellow-50 border-t">
              <div
                className="flex items-center gap-2 p-3 cursor-pointer hover:bg-yellow-100 transition-colors"
                onClick={() => toggle('unassigned')}
              >
                <ExpandIcon nodeKey="unassigned" />
                <AlertCircle size={18} className="text-yellow-600" />
                <span className="font-semibold text-yellow-800">UNASSIGNED TEAM LEADERS</span>
                <span className="text-xs text-yellow-500 ml-1">({unassigned_tls.length})</span>
              </div>
              {isOpen('unassigned') && (
                <div className="pb-2">
                  {unassigned_tls.map((tl, idx) => (
                    <div key={idx} className="flex items-center gap-2 pl-10 pr-3 py-1.5 text-sm">
                      <UserCheck size={14} className="text-yellow-500 shrink-0" />
                      <span className="font-medium">{tl.name}</span>
                      {tl.email && (
                        <a href={`mailto:${tl.email}`} className="text-blue-600 hover:underline text-xs">{tl.email}</a>
                      )}
                      {tl.linked_id && <span className="text-xs text-gray-400">[{tl.linked_id}]</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============= WTF LIVE SYNC TAB =============
  const FEED_LABELS = {
    targets: 'Targets',
    checkin_stats: 'Check-in Stats',
    attendance_stats: 'Attendance Stats',
    late_checkin_stats: 'Late Check-in Stats',
    late_checkins: 'Late Check-ins',
    lead_stats: 'Lead Stats',
    walkin_stats: 'Walk-in Stats',
    member_count: 'Member Count',
    gyms: 'Gyms',
    dashboard_stats: 'Dashboard Stats',
  };

  const WtfSyncTab = () => {
    const { feeds, loading: feedsLoading } = useWtfSyncStatus(5000);
    const [syncing, setSyncing] = useState({});
    const [logs, setLogs] = useState([]);
    const [showLogs, setShowLogs] = useState(false);

    const triggerSync = async (feedName) => {
      setSyncing(prev => ({ ...prev, [feedName]: true }));
      try {
        await axiosInstance.post(`/wtf/sync/trigger/${feedName}`);
        toast.success(`Sync triggered for ${feedName}`);
      } catch (e) {
        toast.error(`Manual sync failed for ${feedName}: ${e.response?.data?.detail || e.message}`);
      } finally {
        setSyncing(prev => ({ ...prev, [feedName]: false }));
      }
    };

    const triggerAll = async () => {
      const allSyncing = {};
      (feeds || []).forEach(f => { allSyncing[f.feed] = true; });
      setSyncing(allSyncing);
      try {
        await axiosInstance.post('/wtf/sync/trigger-all');
        toast.success('All feeds sync triggered');
      } catch (e) {
        toast.error(`Sync all failed: ${e.response?.data?.detail || e.message}`);
      } finally {
        setSyncing({});
      }
    };

    const fetchLogs = async () => {
      try {
        const res = await axiosInstance.get('/wtf/sync/logs?limit=50');
        setLogs(res.data.logs || []);
        setShowLogs(true);
      } catch (e) {
        toast.error('Failed to fetch sync logs');
      }
    };

    const formatSyncAge = (syncedAt) => {
      if (!syncedAt) return 'Never';
      const seconds = Math.floor((Date.now() - new Date(syncedAt).getTime()) / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
      return `${Math.floor(seconds / 3600)}h ago`;
    };

    if (feedsLoading) {
      return <div className="flex items-center justify-center h-64"><div className="loading-spinner" /></div>;
    }

    return (
      <div data-testid="wtf-sync-tab-content">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">WTF API Live Sync</h3>
              <p className="text-sm text-gray-500">Real-time data sync from api.wtfgyms.com</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={triggerAll}
                disabled={Object.values(syncing).some(v => v)}
                size="sm"
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <RefreshCw size={16} className={Object.values(syncing).some(v => v) ? 'animate-spin mr-2' : 'mr-2'} />
                Sync All Now
              </Button>
              <Button
                onClick={fetchLogs}
                variant="outline"
                size="sm"
              >
                View Logs
              </Button>
            </div>
          </div>

          {/* Feed Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {(feeds || []).map(feed => {
              const secondsAgo = feed.synced_at
                ? Math.floor((Date.now() - new Date(feed.synced_at).getTime()) / 1000)
                : null;
              const status = secondsAgo === null ? 'offline'
                : secondsAgo > 120 ? 'stale'
                : secondsAgo > 60 ? 'warning'
                : 'healthy';
              const statusColors = {
                healthy: 'border-green-200 bg-green-50',
                warning: 'border-yellow-200 bg-yellow-50',
                stale: 'border-red-200 bg-red-50',
                offline: 'border-gray-200 bg-gray-50'
              };
              const dotColors = {
                healthy: 'bg-green-500 animate-pulse',
                warning: 'bg-yellow-500',
                stale: 'bg-red-500',
                offline: 'bg-gray-400'
              };
              const statusLabels = {
                healthy: 'Healthy',
                warning: 'Warning',
                stale: 'Stale',
                offline: 'Offline'
              };

              return (
                <div key={feed.feed} className={`rounded-lg border-2 p-4 ${statusColors[status]}`} data-testid={`wtf-feed-card-${feed.feed}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${dotColors[status]}`}></span>
                      <span className="font-medium text-sm">{FEED_LABELS[feed.feed] || feed.feed.replace(/_/g, ' ')}</span>
                    </div>
                    <button
                      onClick={() => triggerSync(feed.feed)}
                      disabled={syncing[feed.feed]}
                      className="text-xs text-purple-600 hover:text-purple-800 font-medium disabled:opacity-50"
                    >
                      {syncing[feed.feed] ? (
                        <span className="flex items-center gap-1">
                          <RefreshCw size={12} className="animate-spin" /> Syncing...
                        </span>
                      ) : 'Sync'}
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>Status: <span className={`font-medium ${
                      status === 'healthy' ? 'text-green-700' :
                      status === 'warning' ? 'text-yellow-700' :
                      status === 'stale' ? 'text-red-700' :
                      'text-gray-700'
                    }`}>{statusLabels[status]}</span></p>
                    <p>Records: <span className="font-medium text-gray-700">{feed.count ?? '--'}</span></p>
                    <p>Last sync: <span className="font-medium text-gray-700">{formatSyncAge(feed.synced_at)}</span></p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Empty state */}
          {(!feeds || feeds.length === 0) && (
            <div className="text-center py-12 text-gray-500">
              <RefreshCw size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No WTF sync feeds configured</p>
              <p className="text-sm mt-1">The WTF API sync scheduler needs to be running to populate feed data.</p>
            </div>
          )}

          {/* Sync Logs (expandable) */}
          {showLogs && (
            <div className="bg-gray-50 rounded-lg border p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-sm">Recent Sync Logs ({logs.length})</h4>
                <button onClick={() => setShowLogs(false)} className="text-xs text-gray-500 hover:text-gray-700">
                  <X size={14} />
                </button>
              </div>
              {logs.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No sync logs found.</p>
              ) : (
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="pb-2 pr-4">Job</th>
                        <th className="pb-2 pr-4">Status</th>
                        <th className="pb-2 pr-4">Duration</th>
                        <th className="pb-2">Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {logs.map((log, i) => (
                        <tr key={i}>
                          <td className="py-1.5 pr-4">{log.job_id || log.feed || '--'}</td>
                          <td className="py-1.5 pr-4">
                            <span className={`px-1.5 py-0.5 rounded text-xs ${
                              log.status === 'success' ? 'bg-green-100 text-green-700' :
                              log.status === 'error' || log.status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>{log.status}</span>
                          </td>
                          <td className="py-1.5 pr-4">{log.duration_ms ? `${log.duration_ms}ms` : '--'}</td>
                          <td className="py-1.5">{log.started_at ? new Date(log.started_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', timeStyle: 'medium', dateStyle: 'short' }) : '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="master-config-page">
      <div className="page-header">
        <h1 className="page-title">Master Configuration</h1>
        <p className="page-subtitle">Manage organizational structure, sources, pulses, and performance settings</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Tabs defaultValue="managers">
            <TabsList className="flex flex-wrap gap-1 mb-6 h-auto">
              <TabsTrigger value="hierarchy" data-testid="hierarchy-tab" className="text-xs py-2">Team Hierarchy</TabsTrigger>
              <TabsTrigger value="managers" data-testid="managers-tab" className="text-xs py-2">Managers</TabsTrigger>
              <TabsTrigger value="tls" data-testid="tls-tab" className="text-xs py-2">Team Leaders</TabsTrigger>
              <TabsTrigger value="fms" data-testid="fms-tab" className="text-xs py-2">Fitness Mgrs</TabsTrigger>
              <TabsTrigger value="gyms" data-testid="gyms-tab" className="text-xs py-2">Gyms</TabsTrigger>
              <TabsTrigger value="frontdesk" data-testid="frontdesk-tab" className="text-xs py-2">Front Desk</TabsTrigger>
              <TabsTrigger value="sources" data-testid="sources-tab" className="text-xs py-2">Sources</TabsTrigger>
              <TabsTrigger value="pulses" data-testid="pulses-tab" className="text-xs py-2">Pulses</TabsTrigger>
              <TabsTrigger value="benchmarks" data-testid="benchmarks-tab" className="text-xs py-2">Benchmarks</TabsTrigger>
              <TabsTrigger value="targets" data-testid="targets-tab" className="text-xs py-2">Targets</TabsTrigger>
              <TabsTrigger value="users" data-testid="users-tab" className="text-xs py-2">Users</TabsTrigger>
              <TabsTrigger value="gym-sync" data-testid="gym-sync-tab" className="text-xs py-2">Gym Sync</TabsTrigger>
              <TabsTrigger value="hq-settings" data-testid="hq-settings-tab" className="text-xs py-2">HQ Settings</TabsTrigger>
              <TabsTrigger value="wtf-sync" data-testid="wtf-sync-tab" className="text-xs py-2">WTF Live Sync</TabsTrigger>
              <TabsTrigger value="lead-mapping" data-testid="lead-mapping-tab" className="text-xs py-2">Lead Mapping</TabsTrigger>
            </TabsList>

            <TabsContent value="hierarchy"><TeamHierarchyTab /></TabsContent>
            <TabsContent value="managers"><ManagersTab /></TabsContent>
            <TabsContent value="tls"><TeamLeadersTab /></TabsContent>
            <TabsContent value="fms"><FitnessManagersTab /></TabsContent>
            <TabsContent value="gyms"><GymsTab /></TabsContent>
            <TabsContent value="frontdesk"><FrontDeskTab /></TabsContent>
            <TabsContent value="sources"><SourcesTab /></TabsContent>
            <TabsContent value="pulses"><PulsesTab /></TabsContent>
            <TabsContent value="benchmarks"><BenchmarksTab /></TabsContent>
            <TabsContent value="targets"><TargetsTab /></TabsContent>
            <TabsContent value="users"><UsersTab /></TabsContent>
            <TabsContent value="gym-sync"><GymSyncTab /></TabsContent>
            <TabsContent value="hq-settings"><HQSettingsTab /></TabsContent>
            <TabsContent value="wtf-sync"><WtfSyncTab /></TabsContent>
            <TabsContent value="lead-mapping"><LeadSourceMappingTab /></TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
